import { useCallback } from 'react';

import { usePersistentState } from '@cast/utils';

import { STEP_INDEX_TO_SCREEN } from '../_constants';
import { AutoOptimizationScreen, AutoOptimizationWizardState } from '../_types';

const STATE_KEY = 'auto-optimization-wizard-v1.beta';

type PersistedAutoPilotRecord = Record<string, AutoOptimizationWizardState>;

export const INITIAL_STATE: AutoOptimizationWizardState = {
  preferences: { priority: null, clusterUsage: null },
  features: { nodeAutoscaling: false, workloadAutoscaling: false },
  excludedNamespaces: [],
  recommendations: {
    data: null,
    isLoading: false,
    error: null,
  },
  applyState: null,
  currentScreen: STEP_INDEX_TO_SCREEN[0],
};

export const useAutoPilotPersistedState = (clusterId: string) => {
  const [state, _setPersisted] = usePersistentState<PersistedAutoPilotRecord>(
    STATE_KEY,
    {}
  );
  const setState = useCallback(
    (
      updater:
        | AutoOptimizationWizardState
        | ((prev: AutoOptimizationWizardState) => AutoOptimizationWizardState)
    ) => {
      _setPersisted((prevRecord) => {
        const prevState = prevRecord[clusterId] ?? INITIAL_STATE;
        const nextState =
          typeof updater === 'function' ? updater(prevState) : updater;
        return { ...prevRecord, [clusterId]: nextState };
      });
    },
    [clusterId, _setPersisted]
  );

  const stored = state[clusterId];
  if (!stored) return [INITIAL_STATE, setState] as const;

  const validScreens = new Set<string>(Object.values(AutoOptimizationScreen));
  const normalized = validScreens.has(stored.currentScreen)
    ? stored
    : { ...stored, currentScreen: INITIAL_STATE.currentScreen };

  return [normalized, setState] as const;
};
